/**
 * Label-management dropdown for the Ads Manager bulk-action toolbar.
 *
 * Trigger: a button with a tag icon. Click opens a popup containing:
 *   - List of existing labels in the org. Each row shows the label
 *     chip (with deterministic colour) plus a × button to delete.
 *     Click on the chip itself toggles "applied to selected entities".
 *   - "+ Create new label" link → expands an inline input + Save.
 *
 * Apply / unapply is bulk: the caller passes the selected entity ids
 * + entity type, so a single dropdown action calls the backend for
 * all selected at once.
 *
 * Empty state ("no labels yet") shows a friendly hint instead of an
 * empty list — the create-form is still available below it.
 */

import { useEffect, useRef, useState } from "react";
import { Popover } from "@/pages/campaign-studio/components/Popover";
import { useLayer } from "@/lib/layer-stack";
import { I } from "@/components/Icon";
import { LabelChip } from "@/components/LabelChip";
import {
  useApplyLabel,
  useCreateLabel,
  useDeleteLabel,
  useLabels,
  useUnapplyLabel,
} from "@/lib/api-hooks";
import type { Label } from "@/lib/types";

export function LabelDropdown({
  entityType,
  entityIds,
  disabled = false,
  value,
  onChange,
  error: fieldError = false,
}: {
  entityType: "campaign" | "ad_group";
  entityIds: number[];
  disabled?: boolean;
  /** Режим выбора (Broad to SKAG Extractor): отмечаются id из `value`,
   *  клик меняет выбор, а не вешает лейбл на сущности. Справочник,
   *  создание и удаление — те же, что в Ads Manager. Триггер — поле с
   *  чипами выбранных. */
  value?: number[];
  onChange?: (ids: number[]) => void;
  error?: boolean;
}) {
  const pickMode = value !== undefined && onChange !== undefined;
  const labelsQuery = useLabels(entityType);
  const create = useCreateLabel();
  const remove = useDeleteLabel();
  const apply = useApplyLabel();
  const unapply = useUnapplyLabel();

  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLButtonElement>(null);

  // Click-outside closes. Escape comes through the layer stack: while the
  // "new label" input is open the first Escape only cancels that input,
  // the next one closes the dropdown.
  useLayer(open, () => {
    if (creating) {
      setCreating(false);
      setNewName("");
      setError(null);
      return;
    }
    setOpen(false);
    setCreating(false);
    setError(null);
  });
  useEffect(() => {
    // В режиме выбора меню в портале, и снаружи его закрывает Popover.
    if (!open || pickMode) return;
    const onClick = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setCreating(false);
        setError(null);
      }
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open, pickMode]);

  const labels: Label[] = labelsQuery.data ?? [];
  const selectedSet = new Set(entityIds);
  const isAppliedToAll = (lab: Label): boolean =>
    entityIds.length > 0 &&
    entityIds.every((id) =>
      lab.applied_to.some(
        (r) => r.entity_type === entityType && r.entity_id === id
      )
    );
  const isAppliedToSome = (lab: Label): boolean =>
    !isAppliedToAll(lab) &&
    lab.applied_to.some(
      (r) => r.entity_type === entityType && selectedSet.has(r.entity_id)
    );

  const handleToggle = async (lab: Label) => {
    setError(null);
    if (pickMode) {
      onChange(value.includes(lab.id) ? value.filter((x) => x !== lab.id) : [...value, lab.id]);
      return;
    }
    if (entityIds.length === 0) return;
    try {
      if (isAppliedToAll(lab)) {
        await unapply.mutateAsync({
          label_id: lab.id,
          entity_type: entityType,
          entity_ids: entityIds,
        });
      } else {
        await apply.mutateAsync({
          label_id: lab.id,
          entity_type: entityType,
          entity_ids: entityIds,
        });
      }
    } catch (e) {
      setError(asErrorMessage(e));
    }
  };

  const handleDelete = async (lab: Label, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm(`Delete label "${lab.name}"? It will be removed from all campaigns and ad groups.`)) {
      return;
    }
    setError(null);
    try {
      await remove.mutateAsync(lab.id);
      if (pickMode && value.includes(lab.id)) onChange(value.filter((x) => x !== lab.id));
    } catch (err) {
      setError(asErrorMessage(err));
    }
  };

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    setError(null);
    try {
      const created = await create.mutateAsync({ name, entity_type: entityType });
      if (pickMode && created?.id != null) onChange([...value, created.id]);
      setNewName("");
      setCreating(false);
    } catch (err) {
      setError(asErrorMessage(err));
    }
  };

  const picked = pickMode
    ? value.map((id) => labels.find((l) => l.id === id)).filter((l): l is Label => l != null)
    : [];
  const rowChecked = (lab: Label) => (pickMode ? value.includes(lab.id) : isAppliedToAll(lab));
  const rowPartial = (lab: Label) => (pickMode ? false : isAppliedToSome(lab));
  const canToggle = pickMode || entityIds.length > 0;

  return (
    <div
      ref={wrapperRef}
      style={{ position: "relative", display: pickMode ? "block" : "inline-block", maxWidth: pickMode ? 520 : undefined }}
    >
      {/* ★ Без `sm`: `.btn.icon.sm` — это 28×28, а соседи по панели
          (Enable, Pause, «Performance over time») идут через `Btn` и
          получают `.btn.icon` — 34×34. Четыре иконки в один ряд обязаны
          быть одного размера; единственное место использования — ровно
          эта панель, так что модификатор здесь ничему не служил. */}
      {pickMode ? (
        <button
          type="button"
          ref={fieldRef}
          className="label-pick-field"
          data-open={open ? "true" : "false"}
          data-error={fieldError ? "true" : "false"}
          disabled={disabled}
          onClick={() => setOpen((v) => !v)}
        >
          <span className="label-pick-chips">
            {picked.length === 0 ? (
              <span className="label-pick-ph">Select labels</span>
            ) : (
              picked.map((l) => <LabelChip key={l.id} name={l.name} />)
            )}
          </span>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7084" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
        </button>
      ) : (
        <button
          type="button"
          className="btn icon"
          disabled={disabled}
          onClick={() => setOpen((v) => !v)}
          title="Manage labels"
          aria-label="Manage labels"
        >
          {I.tag}
        </button>
      )}

      {open && !pickMode && renderMenu({
            position: "absolute",
            top: "calc(100% + 4px)",
            right: 0,
          })}
      {pickMode && (
        <Popover
          open={open}
          anchorRef={fieldRef as React.RefObject<HTMLElement>}
          onClose={() => {
            setOpen(false);
            setCreating(false);
            setError(null);
          }}
          matchAnchorWidth={false}
          width={280}
          gap={4}
        >
          {renderMenu({})}
        </Popover>
      )}
    </div>
  );

  function renderMenu(place: React.CSSProperties) {
    return (
        <div
          role="menu"
          style={{
            ...place,
            minWidth: 280,
            maxHeight: 380,
            overflowY: "auto",
            background: "var(--c-surface, white)",
            border: "1px solid var(--c-border, #ddd)",
            borderRadius: 8,
            boxShadow: "0 8px 24px rgba(0,0,0,.12)",
            zIndex: 50,
            padding: 4,
          }}
        >
          {labelsQuery.isLoading && (
            <div style={{ padding: 12, fontSize: 13, color: "var(--c-text-3)" }}>
              Loading…
            </div>
          )}

          {!labelsQuery.isLoading && labels.length === 0 && !creating && (
            <div style={{ padding: 12, fontSize: 13, color: "var(--c-text-3)" }}>
              No labels yet. Create your first below.
            </div>
          )}

          {labels.map((lab) => {
            const allApplied = rowChecked(lab);
            const partial = rowPartial(lab);
            return (
              <div
                key={lab.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 10px",
                  borderRadius: 6,
                  cursor: canToggle ? "pointer" : "default",
                }}
                onClick={() => canToggle && handleToggle(lab)}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "var(--c-surface-alt, #f5f5f5)")
                }
                onMouseLeave={(e) => (e.currentTarget.style.background = "")}
              >
                <input
                  type="checkbox"
                  checked={allApplied}
                  ref={(el) => {
                    if (el) el.indeterminate = partial;
                  }}
                  readOnly
                  style={{ cursor: "inherit" }}
                />
                <span style={{ flex: 1 }}>
                  <LabelChip name={lab.name} />
                </span>
                <button
                  type="button"
                  aria-label={`Delete label ${lab.name}`}
                  onClick={(e) => handleDelete(lab, e)}
                  style={{
                    background: "transparent",
                    border: 0,
                    color: "var(--c-text-3, #999)",
                    cursor: "pointer",
                    padding: "0 4px",
                    fontSize: 14,
                  }}
                >
                  ×
                </button>
              </div>
            );
          })}

          <div style={{ borderTop: "1px solid var(--c-divider, #eee)", margin: "4px 0" }} />

          {!creating ? (
            <button
              type="button"
              onClick={() => setCreating(true)}
              style={{
                width: "100%",
                textAlign: "left",
                padding: "8px 10px",
                background: "transparent",
                border: 0,
                cursor: "pointer",
                fontSize: 13,
                color: "var(--c-accent-600, #2962ff)",
                borderRadius: 6,
              }}
            >
              + Create new label
            </button>
          ) : (
            <div style={{ display: "flex", gap: 6, padding: "6px 10px" }}>
              <input
                autoFocus
                className="input sm"
                placeholder="Label name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void handleCreate();
                }}
                style={{ flex: 1 }}
              />
              <button
                type="button"
                className="btn sm primary"
                onClick={() => void handleCreate()}
                disabled={!newName.trim() || create.isPending}
              >
                Save
              </button>
            </div>
          )}

          {error && (
            <div
              style={{
                padding: "4px 10px",
                color: "var(--c-neg-600)",
                fontSize: 12,
              }}
            >
              {error}
            </div>
          )}
        </div>
    );
  }
}

function asErrorMessage(e: unknown): string {
  if (typeof e === "object" && e !== null && "response" in e) {
    const resp = (e as { response?: { data?: { error?: { message?: string } } } })
      .response;
    return resp?.data?.error?.message ?? "Something went wrong";
  }
  return e instanceof Error ? e.message : "Something went wrong";
}
